import { apiService } from './api';
import {
  getGcsV4SignedMediaExpiryMs,
  isBoundedPublicMediaUri,
  isStorageMediaUri,
  pickDurableMediaUri,
  pickMediaUri,
} from '../utils/mediaSources';

interface CachedResolvedUrl {
  url: string;
  expiresAt: number | null;
}

interface CachedPreviewUrl extends CachedResolvedUrl {
  authority: string;
  ready: boolean;
  failed: boolean;
  lastReadyUrl: string | null;
  epoch: number;
}

interface InflightResolution {
  epoch: number;
  uriEpoch: number;
  request: Promise<string | null>;
}

interface PreviewCacheContext {
  cacheKey?: string | null;
  durableSource?: string | null;
}

interface ResolveUrlOptions extends PreviewCacheContext {
  forceRefresh?: boolean;
  videoId?: string | null;
  segmentId?: string | null;
}

export type MediaResolutionContext = Pick<
  ResolveUrlOptions,
  'videoId' | 'segmentId' | 'cacheKey' | 'durableSource'
>;

class MediaUrlService {
  private readonly cache = new Map<string, CachedResolvedUrl>();
  private readonly previewCache = new Map<string, CachedPreviewUrl>();
  private readonly inflight = new Map<string, InflightResolution>();
  private readonly uriEpochs = new Map<string, number>();
  private readonly expirySkewMs = 30 * 1000;
  private readonly opaqueGrantPreviewTtlMs = 60 * 1000;
  // Four slots fill an 8-12 tile viewport in a few short waves while still
  // bounding cold-gallery signing traffic across 50+ mounted tiles.
  private readonly maxConcurrentResolutions = 4;
  private readonly maxResolvedEntries = 2000;
  private readonly maxPreviewEntries = 2000;
  private activeResolutions = 0;
  private readonly waitQueue: Array<() => void> = [];
  private readonly activeRequestControllers = new Map<
    AbortController,
    { uri: string; epoch: number }
  >();
  private cacheEpoch = 0;
  private resolutionsSuspended = false;

  private uriEpoch(uri: string): number {
    return this.uriEpochs.get(uri) || 0;
  }

  private inflightKey(uri: string, forceRefresh: boolean): string {
    return `${forceRefresh ? 'forced' : 'normal'}\u0000${uri}`;
  }

  private isCurrentResolution(
    uri: string,
    cacheEpoch: number,
    uriEpoch: number,
  ): boolean {
    return (
      cacheEpoch === this.cacheEpoch
      && uriEpoch === this.uriEpoch(uri)
    );
  }

  private isFresh(entry: CachedResolvedUrl | undefined): entry is CachedResolvedUrl {
    if (!entry) {
      return false;
    }
    if (entry.expiresAt === null) {
      return true;
    }
    return Date.now() < entry.expiresAt - this.expirySkewMs;
  }

  private readResolvedEntry(uri: string): CachedResolvedUrl | null {
    const entry = this.cache.get(uri);
    if (!this.isFresh(entry)) {
      if (entry) this.cache.delete(uri);
      return null;
    }
    this.cache.delete(uri);
    this.cache.set(uri, entry);
    return entry;
  }

  private cacheResolvedUrl(uri: string, entry: CachedResolvedUrl): void {
    this.cache.delete(uri);
    this.cache.set(uri, entry);
    while (this.cache.size > this.maxResolvedEntries) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey === undefined) break;
      this.cache.delete(oldestKey);
    }
  }

  private normalizePreviewCacheKey(cacheKey?: string | null): string | null {
    const normalized = String(cacheKey || '').trim();
    return normalized || null;
  }

  private getPreviewAuthority(
    uri?: string | null,
    durableSource?: string | null,
  ): string | null {
    return pickDurableMediaUri(durableSource, uri) || pickMediaUri(uri);
  }

  private getDirectUrlExpiryMs(url: string): number | null {
    return getGcsV4SignedMediaExpiryMs(url)
      ?? (isBoundedPublicMediaUri(url) ? Date.now() + this.opaqueGrantPreviewTtlMs : null);
  }

  private canCacheDirectPreviewUrl(url: string): boolean {
    // Blob URLs are revocable and base64 payloads are already held by the
    // result projection. Opaque product grants are retained only for the
    // conservative TTL assigned by getDirectUrlExpiryMs.
    return !/^(?:blob:|data:)/i.test(url);
  }

  private readPreviewEntry(
    cacheKey?: string | null,
    authority?: string | null,
    requireFresh = true,
  ): CachedPreviewUrl | null {
    const normalizedKey = this.normalizePreviewCacheKey(cacheKey);
    if (!normalizedKey) return null;
    const entry = this.previewCache.get(normalizedKey);
    if (
      !entry
      || entry.epoch !== this.cacheEpoch
      || (authority && entry.authority !== authority && entry.url !== authority)
    ) {
      if (entry && entry.epoch !== this.cacheEpoch) {
        this.previewCache.delete(normalizedKey);
      }
      return null;
    }
    if (requireFresh && (entry.failed || !this.isFresh(entry))) {
      // A decoded bitmap is still useful while its authorization grant is
      // being replaced. Keep that display fallback in the preview registry,
      // but never return the stale/failed URL as a fresh resolution.
      if (!entry.ready && !entry.lastReadyUrl) {
        this.previewCache.delete(normalizedKey);
      }
      return null;
    }
    // Refresh LRU insertion order without extending URL lifetime.
    this.previewCache.delete(normalizedKey);
    this.previewCache.set(normalizedKey, entry);
    return entry;
  }

  private cachePreviewUrl(
    cacheKey: string | null | undefined,
    authority: string | null,
    url: string,
    expiresAt: number | null,
  ): void {
    const normalizedKey = this.normalizePreviewCacheKey(cacheKey);
    if (!normalizedKey || !authority || !this.canCacheDirectPreviewUrl(url)) {
      return;
    }
    const existing = this.previewCache.get(normalizedKey);
    const isSameUrl = existing?.url === url;
    const lastReadyUrl = isSameUrl
      ? existing?.lastReadyUrl || null
      : existing?.ready
        ? existing.url
        : existing?.lastReadyUrl || null;
    this.previewCache.delete(normalizedKey);
    this.previewCache.set(normalizedKey, {
      url,
      authority,
      expiresAt,
      ready: Boolean(isSameUrl && existing?.ready),
      failed: Boolean(isSameUrl && existing?.failed),
      lastReadyUrl,
      epoch: this.cacheEpoch,
    });
    while (this.previewCache.size > this.maxPreviewEntries) {
      const oldestKey = this.previewCache.keys().next().value;
      if (oldestKey === undefined) break;
      this.previewCache.delete(oldestKey);
    }
  }

  /**
   * A storage request is single-flighted by durable URI, while preview
   * readiness is tracked by each caller's stable asset key. Adopt the shared
   * result into the joining caller's alias so remounting that tile can restore
   * synchronously instead of resolving the same media again.
   */
  private adoptInflightPreview(
    uri: string,
    request: Promise<string | null>,
    context: PreviewCacheContext,
  ): Promise<string | null> {
    return request.then((resolvedUrl) => {
      if (!resolvedUrl) return resolvedUrl;
      const resolvedEntry = this.readResolvedEntry(uri);
      this.cachePreviewUrl(
        context.cacheKey,
        this.getPreviewAuthority(uri, context.durableSource),
        resolvedUrl,
        resolvedEntry?.url === resolvedUrl
          ? resolvedEntry.expiresAt
          : this.getDirectUrlExpiryMs(resolvedUrl),
      );
      return resolvedUrl;
    });
  }

  needsResolution(uri?: string | null): boolean {
    return isStorageMediaUri(uri);
  }

  peekResolvedUrl(
    uri?: string | null,
    preview: PreviewCacheContext = {},
  ): string | null {
    const normalized = pickMediaUri(uri);
    const authority = this.getPreviewAuthority(normalized, preview.durableSource);
    const cachedPreview = this.readPreviewEntry(preview.cacheKey, authority);
    if (cachedPreview) {
      return cachedPreview.url;
    }
    if (!normalized) {
      return null;
    }
    if (!this.needsResolution(normalized)) {
      this.cachePreviewUrl(
        preview.cacheKey,
        authority,
        normalized,
        this.getDirectUrlExpiryMs(normalized),
      );
      return normalized;
    }

    const cached = this.readResolvedEntry(normalized);
    if (!cached) {
      return null;
    }
    this.cachePreviewUrl(
      preview.cacheKey,
      authority,
      cached.url,
      cached.expiresAt,
    );
    return cached.url;
  }

  isPreviewReady(cacheKey?: string | null, url?: string | null): boolean {
    const entry = this.readPreviewEntry(cacheKey, null, false);
    if (!entry) return false;
    if (!url) return entry.ready || Boolean(entry.lastReadyUrl);
    return (
      (entry.ready && entry.url === url)
      || entry.lastReadyUrl === url
    );
  }

  /** Return the most recently decoded URL even if its grant is rotating. */
  peekReadyPreviewUrl(
    cacheKey?: string | null,
    authority?: string | null,
  ): string | null {
    const entry = this.readPreviewEntry(cacheKey, authority, false);
    if (!entry) return null;
    if (entry.ready) return entry.url;
    return entry.lastReadyUrl;
  }

  markPreviewReady(cacheKey?: string | null, url?: string | null): void {
    const normalizedKey = this.normalizePreviewCacheKey(cacheKey);
    const normalizedUrl = pickMediaUri(url);
    if (!normalizedKey || !normalizedUrl) return;
    const entry = this.readPreviewEntry(normalizedKey, null, false);
    if (!entry) return;
    if (entry.url === normalizedUrl) {
      entry.ready = true;
      entry.failed = false;
      entry.lastReadyUrl = null;
    }
  }

  markPreviewFailed(cacheKey?: string | null, url?: string | null): void {
    const normalizedKey = this.normalizePreviewCacheKey(cacheKey);
    const normalizedUrl = pickMediaUri(url);
    if (!normalizedKey) return;
    const entry = this.previewCache.get(normalizedKey);
    if (!entry || (normalizedUrl && entry.url !== normalizedUrl)) return;
    if (entry.ready || entry.lastReadyUrl) {
      entry.failed = true;
      return;
    }
    this.previewCache.delete(normalizedKey);
  }

  /**
   * Return the time until a cached storage URL should be refreshed. Consumers
   * use this only as a cache-lifetime signal; the backend remains responsible
   * for authorizing every replacement URL.
   */
  getRefreshDelayMs(
    uri?: string | null,
    now = Date.now(),
    preview: PreviewCacheContext = {},
  ): number | null {
    const normalized = pickMediaUri(uri);
    const authority = this.getPreviewAuthority(normalized, preview.durableSource);
    const previewEntry = this.readPreviewEntry(preview.cacheKey, authority);
    const directExpiry = normalized ? this.getDirectUrlExpiryMs(normalized) : null;
    const resolvedEntry = normalized && this.needsResolution(normalized)
      ? this.readResolvedEntry(normalized)
      : null;
    const expiresAt = previewEntry?.expiresAt ?? resolvedEntry?.expiresAt ?? directExpiry;
    if (!normalized || expiresAt === null) {
      return null;
    }
    const delayMs = expiresAt - this.expirySkewMs - now;
    // An already-expired or skew-window URL must not create a tight refresh
    // loop. It remains uncached by peekResolvedUrl and can be retried through
    // the normal error/reload path.
    return delayMs > 0 ? delayMs : null;
  }

  private async scheduleResolution<T>(task: () => Promise<T>): Promise<T> {
    if (this.activeResolutions >= this.maxConcurrentResolutions) {
      await new Promise<void>((resolve) => {
        this.waitQueue.push(resolve);
      });
    }

    this.activeResolutions += 1;
    try {
      return await task();
    } finally {
      this.activeResolutions = Math.max(0, this.activeResolutions - 1);
      const next = this.waitQueue.shift();
      if (next) {
        next();
      }
    }
  }

  async resolveUrl(
    uri?: string | null,
    options: ResolveUrlOptions = {},
  ): Promise<string | null> {
    const normalized = pickMediaUri(uri);
    const forceRefresh = options.forceRefresh === true;
    if (!normalized) {
      return null;
    }
    if (!this.needsResolution(normalized)) {
      this.cachePreviewUrl(
        options.cacheKey,
        this.getPreviewAuthority(normalized, options.durableSource),
        normalized,
        this.getDirectUrlExpiryMs(normalized),
      );
      return normalized;
    }
    if (this.resolutionsSuspended) {
      return null;
    }

    if (!forceRefresh) {
      const forcedInflight = this.inflight.get(
        this.inflightKey(normalized, true),
      );
      const currentUriEpoch = this.uriEpoch(normalized);
      if (
        forcedInflight?.epoch === this.cacheEpoch
        && forcedInflight.uriEpoch === currentUriEpoch
      ) {
        return this.adoptInflightPreview(normalized, forcedInflight.request, options);
      }
    }

    if (!forceRefresh) {
      const cachedUrl = this.peekResolvedUrl(normalized, options);
      if (cachedUrl) {
        return cachedUrl;
      }
    }

    const resolutionKey = this.inflightKey(normalized, forceRefresh);
    const inflight = this.inflight.get(resolutionKey);
    const requestUriEpoch = this.uriEpoch(normalized);
    if (
      inflight?.epoch === this.cacheEpoch
      && inflight.uriEpoch === requestUriEpoch
    ) {
      return this.adoptInflightPreview(normalized, inflight.request, options);
    }

    const requestEpoch = this.cacheEpoch;
    const scheduledRequest = this.scheduleResolution(async () => {
      if (!this.isCurrentResolution(
        normalized,
        requestEpoch,
        requestUriEpoch,
      )) {
        return null;
      }
      const controller = new AbortController();
      this.activeRequestControllers.set(controller, {
        uri: normalized,
        epoch: requestEpoch,
      });
      let response: Awaited<ReturnType<typeof apiService.getSignedUrl>>;
      try {
        response = await apiService.getSignedUrl(
          normalized,
          controller.signal,
          forceRefresh,
          {
            videoId: options.videoId,
            segmentId: options.segmentId,
          },
        );
      } catch (error) {
        // Auth transitions, explicit invalidation, and cache resets abort old
        // credentialed requests so they cannot occupy the bounded semaphore
        // or delay the next account. Their result is intentionally stale.
        if (!this.isCurrentResolution(
          normalized,
          requestEpoch,
          requestUriEpoch,
        )) {
          return null;
        }
        throw error;
      } finally {
        this.activeRequestControllers.delete(controller);
      }
      const expiresAt = response.expires_at ? new Date(response.expires_at).getTime() : null;
      if (!this.isCurrentResolution(
        normalized,
        requestEpoch,
        requestUriEpoch,
      )) {
        return null;
      }
      this.cacheResolvedUrl(normalized, {
        url: response.signed_url,
        expiresAt: Number.isFinite(expiresAt) ? expiresAt : null,
      });
      return response.signed_url;
    });
    let request: Promise<string | null>;
    request = scheduledRequest
      .then(async (resolvedUrl) => {
        if (resolvedUrl !== null || forceRefresh) {
          return resolvedUrl;
        }
        // A browser failure can rotate a grant while an ordinary refresh is
        // already in flight. Adopt that replacement for same-auth-epoch
        // waiters after releasing this request's semaphore slot. Auth scope
        // transitions increment cacheEpoch and must remain terminally stale.
        if (requestEpoch !== this.cacheEpoch) {
          return null;
        }
        const forcedInflight = this.inflight.get(
          this.inflightKey(normalized, true),
        );
        const currentUriEpoch = this.uriEpoch(normalized);
        if (
          forcedInflight?.epoch === this.cacheEpoch
          && forcedInflight.uriEpoch === currentUriEpoch
        ) {
          return forcedInflight.request;
        }
        const currentNormal = this.inflight.get(
          this.inflightKey(normalized, false),
        );
        if (
          currentNormal?.request !== request
          && currentNormal?.epoch === this.cacheEpoch
          && currentNormal.uriEpoch === currentUriEpoch
        ) {
          return currentNormal.request;
        }
        return this.peekResolvedUrl(normalized, options);
      })
      .finally(() => {
        const current = this.inflight.get(resolutionKey);
        if (current?.request === request) {
          this.inflight.delete(resolutionKey);
        }
      });

    this.inflight.set(resolutionKey, {
      epoch: requestEpoch,
      uriEpoch: requestUriEpoch,
      request,
    });
    return this.adoptInflightPreview(normalized, request, options);
  }

  /**
   * Replace a browser-rejected grant exactly once per shared failed URL.
   *
   * Multiple media elements can share one cached grant and fail together.
   * Compare the failed URL before invalidating, then single-flight the forced
   * replacement so a later consumer cannot abort the first consumer's retry.
   */
  async refreshFailedUrl(
    uri?: string | null,
    failedUrl?: string | null,
    context: MediaResolutionContext = {},
  ): Promise<string | null> {
    const normalized = pickMediaUri(uri);
    const normalizedFailedUrl = String(failedUrl || '').trim();
    if (!normalized) {
      return null;
    }
    if (!this.needsResolution(normalized)) {
      this.markPreviewFailed(context.cacheKey, failedUrl);
      return normalized;
    }
    if (this.resolutionsSuspended) {
      return null;
    }

    const forcedKey = this.inflightKey(normalized, true);
    const currentUriEpoch = this.uriEpoch(normalized);
    const forcedInflight = this.inflight.get(forcedKey);
    if (
      forcedInflight?.epoch === this.cacheEpoch
      && forcedInflight.uriEpoch === currentUriEpoch
    ) {
      const replacement = await forcedInflight.request;
      if (replacement) {
        this.cachePreviewUrl(
          context.cacheKey,
          this.getPreviewAuthority(normalized, context.durableSource),
          replacement,
          this.readResolvedEntry(normalized)?.expiresAt ?? this.getDirectUrlExpiryMs(replacement),
        );
      }
      return replacement;
    }

    const cached = this.readResolvedEntry(normalized);
    if (
      cached
      && normalizedFailedUrl
      && cached.url !== normalizedFailedUrl
    ) {
      this.cachePreviewUrl(
        context.cacheKey,
        this.getPreviewAuthority(normalized, context.durableSource),
        cached.url,
        cached.expiresAt,
      );
      return cached.url;
    }

    this.invalidate(normalized, context.cacheKey, true);
    const replacement = await this.resolveUrl(normalized, {
      forceRefresh: true,
      ...context,
    });
    if (replacement && replacement === normalizedFailedUrl) {
      const replacementCache = this.readResolvedEntry(normalized);
      if (replacementCache?.url === replacement) {
        this.invalidate(normalized, context.cacheKey);
      }
      return null;
    }
    return replacement;
  }

  invalidate(
    uri?: string | null,
    cacheKey?: string | null,
    preserveReadyPreview = false,
  ): void {
    const normalized = pickMediaUri(uri);
    const normalizedCacheKey = this.normalizePreviewCacheKey(cacheKey);
    if (normalizedCacheKey) {
      const previewEntry = this.previewCache.get(normalizedCacheKey);
      if (preserveReadyPreview && previewEntry && (previewEntry.ready || previewEntry.lastReadyUrl)) {
        previewEntry.failed = true;
      } else {
        this.previewCache.delete(normalizedCacheKey);
      }
    }
    if (!normalized || !this.needsResolution(normalized)) {
      return;
    }
    this.uriEpochs.set(normalized, this.uriEpoch(normalized) + 1);
    this.activeRequestControllers.forEach((request, controller) => {
      if (request.uri === normalized) {
        controller.abort();
      }
    });
    this.cache.delete(normalized);
    this.inflight.delete(this.inflightKey(normalized, false));
    this.inflight.delete(this.inflightKey(normalized, true));
  }

  clearCache(): void {
    this.cacheEpoch += 1;
    this.activeRequestControllers.forEach((_request, controller) => {
      controller.abort();
    });
    this.activeRequestControllers.clear();
    this.cache.clear();
    this.previewCache.clear();
    this.inflight.clear();
    this.uriEpochs.clear();
  }

  beginUserScopeTransition(): void {
    this.resolutionsSuspended = true;
    this.clearCache();
  }

  activateUserScope(scopeId: string | null): void {
    this.clearCache();
    this.resolutionsSuspended = scopeId === null;
  }

  /** Test/debug surface; does not expose cached credentials or URLs. */
  getCacheStats(): { resolvedUrls: number; previewUrls: number } {
    return {
      resolvedUrls: this.cache.size,
      previewUrls: this.previewCache.size,
    };
  }
}

export const mediaUrlService = new MediaUrlService();
